import { observer } from "mobx-react-lite";
import { Flex, Text } from "@fluentui/react-northstar";
import styles from "./groupLevelRanking.module.scss";
import { ChevronDownIcon } from "@fluentui/react-icons-northstar";
import pl from "shared/messages/pl";
import React, { useContext } from "react";
import RankItem from "../RankItem";
import classNames from "classnames";
import { StarIcon } from "@fluentui/react-icons-northstar";
import { IThemeTeamsClient } from "msTeamsUI/lib/withTheme";
import { MsTeamsContext } from "msTeams/store";
import { getTargetValueFormat } from "shared/reports/utils/ReportUtils";
import { UnitType } from "shared/reports/models/UnityType";

export interface GroupLevelRankingProps {
    rank: number;
    numberOfCompetitors?: number;
    change: number;
    planTarget?: number;
    planValue?: number;
    compact?: boolean;
    unit: UnitType;
}

const GroupLevelRanking: React.FC<GroupLevelRankingProps> = observer((props: GroupLevelRankingProps) => {
    const msTeamsContext = useContext(MsTeamsContext);

    const renderProgressStatus = (change: number) => {
        const arrowRotation = change === 0 ? 270 : change > 0 ? 180 : 0;
        const progressLabel = change > 0 ? `+${change}` : change;
        const arrowContainerClassName = props.compact ? styles.arrowContainerCompact : styles.arrowContainer;
        let arrowContainerBackgroundClassName =
            change === 0
                ? styles.arrowContainerNoChangeProgress
                : change > 0
                ? styles.arrowContainerPositiveProgress
                : styles.arrowContainerNegativeProgress;

        if (change === 0 && msTeamsContext.currentMsTeamsThemeName !== IThemeTeamsClient.Default) {
            arrowContainerBackgroundClassName = styles.arrowContainerNoChangeProgressDarkTheme;
        }

        return (
            <Flex>
                <div className={classNames(arrowContainerClassName, arrowContainerBackgroundClassName)}>
                    <ChevronDownIcon size={props.compact ? "small" : "medium"} rotate={arrowRotation} />
                </div>
                <Text size={props.compact ? "medium" : "large"} weight="bold">
                    {progressLabel}
                </Text>
            </Flex>
        );
    };

    const getRankDispaly = () => {
        if (props.numberOfCompetitors) {
            return `${props.rank} / ${props.numberOfCompetitors}`;
        }
        return props.rank;
    };

    const getPlanTargetText = () => {
        return (
            <>
                {getTargetValueFormat(props.planValue, props.unit)}
                &nbsp;/&nbsp;
                {getTargetValueFormat(props.planTarget, props.unit)}
            </>
        );
    };

    return (
        <Flex fill column gap="gap.small">
            <Flex vAlign="center" fill gap={props.compact ? "gap.small" : "gap.large"}>
                <RankItem header={pl.motivationCard.headers.salesRank.rankPosition}>
                    <Flex>
                        <Text size={props.compact ? "medium" : "large"} weight="semibold">
                            {getRankDispaly()}
                        </Text>
                        {!props.compact && props.rank === 1 && <StarIcon size="small" className={styles.leaderBadge} />}
                    </Flex>
                </RankItem>
                <RankItem header={pl.motivationCard.headers.salesRank.change}>
                    {renderProgressStatus(props.change)}
                </RankItem>
            </Flex>
            {props.compact ? (
                <div className={styles.planTargetValueText}>{getPlanTargetText()}</div>
            ) : (
                <RankItem header={pl.motivationCard.headers.salesRank.planTargetValue}>
                    <Text size="large" weight="regular">
                        {getPlanTargetText()}
                    </Text>
                </RankItem>
            )}
        </Flex>
    );
});

export default GroupLevelRanking;
